/**
 * EmptyState - Empty Content Placeholder
 * 
 * A centered placeholder displayed when lists or tables have no content.
 * Includes an icon, title, and optional description.
 * 
 * @example
 * ```tsx
 * <EmptyState
 *   icon={<GroupIcon className="size-7 text-white" />}
 *   title="No teams added yet"
 *   description="Create your first team to allow multiple members to share one membership"
 * />
 * ```
 * 
 * @module primitives/feedback/EmptyState
 */

import type { ReactNode } from 'react'
import { cn } from '#utils'
import { GradientCircle } from '#primitives/data-display'
import type { GradientVariant } from '#primitives/data-display'

export interface EmptyStateProps {
  /** Icon to display in the gradient circle */
  icon: ReactNode
  /** Title text */
  title: string
  /** Optional description text */
  description?: string
  /** Optional action button/link */
  action?: ReactNode
  /**
   * Icon circle treatment. Any `GradientVariant` paints the usual gradient
   * circle; `'muted'` swaps it for a flat grey one, for lower-emphasis empty
   * states such as a table that still shows its search bar. In `'muted'` the
   * icon supplies its own colour, so pass a tinted icon rather than a white one.
   * `'placeholder'` is the design system's placeholder pattern — a 72px flat
   * grey circle with a hairline border, 16/600 title, and 14/500 body at 130%
   * line-height; the icon supplies its own colour (pass a tinted 32px glyph).
   */
  variant?: GradientVariant | 'muted' | 'placeholder'
  /**
   * Compact scale for the `'placeholder'` variant (drawers, tab panels): 60px
   * circle, 12px description. Ignored by the other variants.
   * @default 'default'
   */
  size?: 'default' | 'compact'
  /** Optional typography overrides for design-specific placeholder contexts. */
  titleClassName?: string
  descriptionClassName?: string
  /** Container className */
  className?: string
  /** Test hook on the container — lets a spec race "list or empty state" for page-ready. */
  'data-testid'?: string
}

/**
 * EmptyState component
 * 
 * Renders a centered empty state with icon, title, and optional description.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  variant = 'purple',
  size = 'default',
  className,
  titleClassName,
  descriptionClassName,
  'data-testid': testId,
}: EmptyStateProps) {
  const isPlaceholder = variant === 'placeholder'
  const isCompact = isPlaceholder && size === 'compact'
  return (
    <div
      data-testid={testId}
      className={cn(
        'flex flex-col items-center justify-center py-16',
        isPlaceholder ? 'gap-4' : 'gap-3',
        className
      )}
    >
      {isPlaceholder ? (
        <div className={`flex ${isCompact ? 'size-15' : 'size-18'} items-center justify-center rounded-full border border-butter-gray-100 bg-butter-gray-50`}>
          {icon}
        </div>
      ) : variant === 'muted' ? (
        <div className="flex size-14 items-center justify-center rounded-full bg-surface-2">
          {icon}
        </div>
      ) : (
        <GradientCircle variant={variant} className="size-14">
          {icon}
        </GradientCircle>
      )}
      <div className="text-center">
        <p
          className={cn(
            isPlaceholder
              ? 'text-base font-semibold text-butter-gray-800'
              : 'text-sm font-semibold text-ink-strong',
            titleClassName,
          )}
        >
          {title}
        </p>
        {description && (
          <p
            className={cn(
              isPlaceholder
                ? `mt-2 max-w-sm ${isCompact ? 'text-xs' : 'text-sm'} font-medium leading-1.3 text-butter-gray-600`
                : 'text-sm text-ink-muted max-w-xs mt-1',
              descriptionClassName,
            )}
          >
            {description}
          </p>
        )}
      </div>
      {action && (
        <div className="mt-2">
          {action}
        </div>
      )}
    </div>
  )
}
